<!-- /////////////////////////////////////////////////////////////////////////// TEMPLATE -->

<template>
    <div class="sl-widget sl-calculator sl-kr0conv" @keydown.esc="emit('close')">
        <div class="sl-widget__header">
            <div class="sl-widget__title">
                <span>{{ $t('calculators.corneal_curve_conversion.title') }}</span>

                <mdi-icon
                    v-tippy
                    :content="$t('common.close')"
                    icon="mdiClose"
                    class="cursor-pointer text-gray-500 hover:text-primary-500 transition"
                    @click="emit('close')"
                />
            </div>
        </div>

        <div class="sl-widget__body">
            <div class="grid grid-cols-2 gap-2 justify-center items-center text-center">
                <div>
                    {{ t('calculators.corneal_curve_conversion.base_curve.label') }}
                </div>

                <div>
                    <lsn-input
                        v-model="strBaseCurveDpt"
                        class="lsn-form__wrapper--padless sl-kr0conv__input"
                    />
                </div>

                <div>
                    {{ t('calculators.corneal_curve_conversion.tears_index.label') }}
                </div>

                <div>
                    <lsn-input
                        v-model="strTearsIndex"
                        class="lsn-form__wrapper--padless sl-kr0conv__input"
                    />
                </div>

                <div>
                    {{ t('calculators.corneal_curve_conversion.result.label') }}
                </div>

                <div>
                    <template v-if="isFinite(baseCurveMm)">
                        {{ baseCurveMm.toFixed(2) }}
                    </template>

                    <template v-else>
                        &ndash;
                    </template>
                </div>
            </div>
        </div>

        <!-- Action Buttons -->
        <div class="sl-widget__actions">
            <div
                v-if="fittings.right"
                class="sl-widget__button"
                :class="attributeButtonClass"
                tabindex="0"
                @click="attribute('right')"
                @keypress.enter="attribute('right')"
            >
                {{ $t('calculators.common.attribute_right') }}
            </div>

            <div
                v-if="fittings.left"
                class="sl-widget__button"
                :class="attributeButtonClass"
                tabindex="0"
                @click="attribute('left')"
                @keypress.enter="attribute('left')"
            >
                {{ $t('calculators.common.attribute_left') }}
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

const defaultTearsIndex = 1.3375;

const emit = defineEmits(['close']);

const { t } = useI18n({ useScope: 'global' });
const store = useStore();

const attributing = ref(false);
const strBaseCurveDpt = ref('30');
const strTearsIndex = ref(defaultTearsIndex.toFixed(4));

const baseCurveMm = computed(() =>
{
    const dpt = parseFloat(strBaseCurveDpt.value.replace(/,/g, '.'));
    if(!isFinite(dpt))
    {
        return NaN;
    }

    const index = parseFloat(strTearsIndex.value.replace(/,/g, '.'));
    if(!isFinite(index))
    {
        return NaN;
    }

    const diok = (index - 1) * 1000;

    return diok / dpt;
});

const attributeButtonClass = computed(() =>
{
    if(attributing.value || !isFinite(baseCurveMm.value))
    {
        return 'sl-widget__button--disabled';
    }

    return 'sl-widget__button--primary';
});

const fittings = computed(() => ({
    right: store.getters['lensOrder/getFitting']('right'),
    left:  store.getters['lensOrder/getFitting']('left'),
}));

async function attribute(side)
{
    if(attributing.value || !isFinite(baseCurveMm.value))
    {
        return;
    }

    if(!['right', 'left'].includes(side))
    {
        throw new RangeError(`Invalid value for side: "${side}"`);
    }

    attributing.value = true;
    const fitting = fittings.value[side];
    fitting.getPrototype().setValue('LENS_R0', baseCurveMm.value);

    try
    {
        await store.dispatch('lensOrder/template', { side });
        emit('close');
    }
    catch(error)
    {
        // todo: handle error
        console.error(error);
    }
    finally
    {
        attributing.value = false;
    }
}
</script>

<style scoped>
@reference "#main.css";

.sl-kr0conv :deep(.sl-kr0conv__input input)
{
    @apply text-center;
}
</style>
